import React, { useEffect, useState, useCallback } from 'react';
import { fetchPetsToday, feedPet, resetFeed, toggleMed, incrementMed, resetMed, fetchWalksToday, toggleWalk } from '../api';

function ScoopBar({ given, target }) {
  const pct = Math.min((given / target) * 100, 100);
  const done = given >= target;
  return (
    <div style={styles.barWrap}>
      <div style={{
        ...styles.barFill,
        width: `${pct}%`,
        background: done ? 'var(--accent3)' : 'var(--accent)',
      }} />
    </div>
  );
}

export default function PetTracker() {
  const [pets, setPets]   = useState([]);
  const [walks, setWalks] = useState({ morning: false, afternoon: false });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [p, w] = await Promise.all([fetchPetsToday(), fetchWalksToday()]);
    setPets(p);
    setWalks(w);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    // Reload at midnight to reset the display
    const now = new Date();
    const msUntilMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1) - now;
    const midnight = setTimeout(load, msUntilMidnight);
    // Also poll every 60s for family sync
    const poll = setInterval(load, 60 * 1000);
    return () => { clearTimeout(midnight); clearInterval(poll); };
  }, [load]);

  const handleFeed = async (petId) => {
    const updated = await feedPet(petId);
    setPets(prev => prev.map(p => p.id === petId
      ? { ...p, scoops_given: updated.scoops_given } : p));
  };

  const handleResetFeed = async (petId) => {
    await resetFeed(petId);
    setPets(prev => prev.map(p => p.id === petId ? { ...p, scoops_given: 0 } : p));
  };

  const handleIncrementMed = async (petId, medId) => {
    const updated = await incrementMed(medId);
    setPets(prev => prev.map(p => {
      if (p.id !== petId) return p;
      return { ...p, meds: p.meds.map(m => m.id === medId ? { ...m, given: updated.given } : m) };
    }));
  };

  const handleToggleMed = async (petId, medId) => {
    const updated = await toggleMed(medId);
    setPets(prev => prev.map(p => {
      if (p.id !== petId) return p;
      return { ...p, meds: p.meds.map(m => m.id === medId ? { ...m, given: updated.given } : m) };
    }));
  };

  const handleResetMed = async (petId, medId) => {
    await resetMed(medId);
    setPets(prev => prev.map(p => {
      if (p.id !== petId) return p;
      return { ...p, meds: p.meds.map(m => m.id === medId ? { ...m, given: 0 } : m) };
    }));
  };

  const handleToggleWalk = async (type) => {
    const updated = await toggleWalk(type);
    setWalks(prev => ({ ...prev, [type]: updated.completed }));
  };

  const dogs = pets.filter(p => p.is_dog);

  if (loading) return <div style={styles.panel}><p style={styles.empty}>Loading…</p></div>;

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <span style={styles.panelLabel}>Pets</span>
        <span style={styles.dot} />
        <span style={styles.resetNote}>Resets at midnight</span>
      </div>

      {/* Walk reminders — only shown if there are dogs */}
      {dogs.length > 0 && (
        <div style={styles.walkRow}>
          {['morning', 'afternoon'].map(type => (
            <button
              key={type}
              style={{
                ...styles.walkBtn,
                ...(walks[type] ? styles.walkBtnDone : styles.walkBtnPending),
              }}
              onClick={() => handleToggleWalk(type)}
            >
              {walks[type] ? '✓' : '🐕'} {type.charAt(0).toUpperCase() + type.slice(1)} Walk
            </button>
          ))}
        </div>
      )}

      {/* Pet rows */}
      <div style={styles.petList}>
        {pets.map(pet => {
          const done = pet.scoops_given >= pet.target_scoops;
          const allMedsDone = pet.meds.length === 0 || pet.meds.every(m => m.given);

          return (
            <div key={pet.id} style={{
              ...styles.petCard,
              ...(done && allMedsDone ? styles.petCardDone : {}),
            }}>
              {/* Pet header */}
              <div style={styles.petHeader}>
                <span style={styles.petName}>{pet.name}</span>
                <span style={styles.petStatus}>
                  {done && allMedsDone ? '✓ All done' : ''}
                </span>
              </div>

              {/* Feeding */}
              <div style={styles.feedRow}>
                <div style={styles.feedInfo}>
                  <span style={styles.feedLabel}>Food</span>
                  <span style={{
                    ...styles.feedCount,
                    color: done ? 'var(--accent3)' : 'var(--accent)',
                  }}>
                    {pet.scoops_given % 1 === 0
                      ? pet.scoops_given
                      : pet.scoops_given.toFixed(1)}
                    {' / '}
                    {pet.target_scoops % 1 === 0
                      ? pet.target_scoops
                      : pet.target_scoops.toFixed(1)} scoops
                  </span>
                </div>
                <div style={styles.feedActions}>
                  {pet.scoops_given > 0 && (
                    <button
                      style={styles.resetBtn}
                      onClick={() => handleResetFeed(pet.id)}
                      title="Reset feeding"
                    >↺</button>
                  )}
                  <button
                    style={{
                      ...styles.feedBtn,
                      ...(done ? styles.feedBtnDone : {}),
                    }}
                    onClick={() => !done && handleFeed(pet.id)}
                    disabled={done}
                  >
                    {done ? '✓' : '+½'}
                  </button>
                </div>
              </div>

              <ScoopBar given={pet.scoops_given} target={pet.target_scoops} />

              {/* Meds */}
              {pet.meds.length > 0 && (
                <div style={styles.medsRow}>
                  {pet.meds.map(med => {
                    const done = med.is_numeric
                      ? med.given >= med.target_count
                      : med.given >= 1;
                    return (
                      <div key={med.id} style={styles.medItem}>
                        {/* Reset button — appears once at least 1 given */}
                        {med.given > 0 && (
                          <button
                            style={styles.medResetBtn}
                            onClick={() => handleResetMed(pet.id, med.id)}
                            title="Reset"
                          >↺</button>
                        )}
                        {/* Tappable chip — increments for numeric, toggles for non-numeric */}
                        <button
                          style={{
                            ...styles.medChip,
                            ...(done ? styles.medChipDone : styles.medChipPending),
                          }}
                          onClick={() => {
                            if (med.is_numeric) {
                              !done && handleIncrementMed(pet.id, med.id);
                            } else {
                              handleToggleMed(pet.id, med.id);
                            }
                          }}
                        >
                          <span style={styles.medChipName}>{med.med_name}</span>
                          {med.is_numeric ? (
                            <span style={styles.medChipCount}>
                              {done ? '✓' : `${med.given}/${med.target_count}`}
                            </span>
                          ) : (
                            <span style={styles.medChipCount}>
                              {done ? '✓' : '○'}
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    height: '100%',
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  panelLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.65rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--text-dimmer)',
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#e87ab8',
    boxShadow: '0 0 6px #e87ab8',
  },
  resetNote: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.58rem',
    color: 'var(--text-dimmer)',
    marginLeft: 'auto',
    letterSpacing: '0.05em',
  },
  walkRow: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
  },
  walkBtn: {
    flex: 1,
    fontFamily: 'var(--font-mono)',
    fontSize: '0.72rem',
    letterSpacing: '0.05em',
    padding: '8px 12px',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.15s',
  },
  walkBtnPending: {
    background: 'rgba(232,122,122,0.12)',
    border: '1px solid rgba(232,122,122,0.4)',
    color: '#e87a7a',
  },
  walkBtnDone: {
    background: 'rgba(122,232,184,0.1)',
    border: '1px solid rgba(122,232,184,0.3)',
    color: 'var(--accent3)',
  },
  petList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    overflowY: 'auto',
    flex: 1,
  },
  petCard: {
    background: 'var(--surface2)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    transition: 'border-color 0.2s',
  },
  petCardDone: {
    borderColor: 'rgba(122,232,184,0.3)',
    background: 'rgba(122,232,184,0.04)',
  },
  petHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },
  petName: {
    fontFamily: 'var(--font-body)',
    fontSize: '0.9rem',
    color: 'var(--text)',
    fontWeight: 500,
    flex: 1,
  },
  petStatus: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.62rem',
    color: 'var(--accent3)',
    letterSpacing: '0.05em',
  },
  feedRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  feedInfo: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
  },
  feedLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.6rem',
    color: 'var(--text-dimmer)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  feedCount: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.78rem',
    fontWeight: 500,
  },
  feedActions: {
    display: 'flex',
    gap: 5,
    alignItems: 'center',
  },
  feedBtn: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.72rem',
    fontWeight: 600,
    background: 'rgba(232,201,122,0.15)',
    border: '1px solid rgba(232,201,122,0.4)',
    color: 'var(--accent)',
    borderRadius: 8,
    padding: '4px 10px',
    cursor: 'pointer',
    letterSpacing: '0.05em',
  },
  feedBtnDone: {
    background: 'rgba(122,232,184,0.1)',
    border: '1px solid rgba(122,232,184,0.3)',
    color: 'var(--accent3)',
    cursor: 'default',
  },
  resetBtn: {
    background: 'none',
    border: '1px solid var(--border)',
    color: 'var(--text-dimmer)',
    borderRadius: 8,
    padding: '4px 7px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    lineHeight: 1,
  },
  barWrap: {
    height: 3,
    background: 'var(--border)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  medsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
    marginTop: 2,
  },
  medChip: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.68rem',
    borderRadius: 20,
    padding: '4px 10px',
    cursor: 'pointer',
    border: 'none',
    letterSpacing: '0.02em',
  },
  medChipPending: {
    background: 'rgba(232,122,184,0.1)',
    border: '1px solid rgba(232,122,184,0.35)',
    color: '#e87ab8',
    cursor: 'pointer',
  },
  medChipDone: {
    background: 'rgba(122,232,184,0.08)',
    border: '1px solid rgba(122,232,184,0.3)',
    color: 'var(--accent3)',
    cursor: 'default',
  },
  medQty: {
    opacity: 0.7,
    fontSize: '0.62rem',
  },
  medChipName: {
    letterSpacing: '0.02em',
  },
  medChipCount: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.72rem',
    fontWeight: 600,
    marginLeft: 6,
    opacity: 0.9,
  },
  medItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  medResetBtn: {
    background: 'none',
    border: '1px solid var(--border)',
    color: 'var(--text-dimmer)',
    borderRadius: 7,
    padding: '4px 7px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    lineHeight: 1,
    flexShrink: 0,
  },
  empty: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--text-dimmer)',
    fontStyle: 'italic',
  },
};
